/**
 * Shared cache of the natural pixel dimensions of R2-served images, keyed
 * by r2_key. The compose editor's sharpness badge and the export
 * preflight both need "how many pixels does this placed image actually
 * have?" — decoding happens once per key via the browser's image cache
 * (the editor has usually rendered the same URL already, so the fetch is
 * warm) and every consumer reads the same map.
 */
import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { r2Url } from './r2Url';

export interface ImageDims {
  w: number;
  h: number;
}

/** undefined = never requested; null = load failed (don't retry). */
const cache = new Map<string, ImageDims | null>();
const pending = new Map<string, Promise<ImageDims | null>>();
const listeners = new Set<() => void>();

function notify() {
  for (const l of listeners) l();
}

/** Resolve (and cache) an image's natural dimensions. Never rejects. */
export function loadImageDims(r2Key: string): Promise<ImageDims | null> {
  const cached = cache.get(r2Key);
  if (cached !== undefined) return Promise.resolve(cached);
  let p = pending.get(r2Key);
  if (!p) {
    p = new Promise<ImageDims | null>((resolve) => {
      const img = new Image();
      const settle = (dims: ImageDims | null) => {
        cache.set(r2Key, dims);
        pending.delete(r2Key);
        notify();
        resolve(dims);
      };
      img.onload = () =>
        settle(
          img.naturalWidth > 0 && img.naturalHeight > 0
            ? { w: img.naturalWidth, h: img.naturalHeight }
            : null,
        );
      img.onerror = () => settle(null);
      img.src = r2Url(r2Key);
    });
    pending.set(r2Key, p);
  }
  return p;
}

/** Cached dimensions, or null when unknown/failed. Does not trigger a load. */
export function getImageDims(r2Key: string): ImageDims | null {
  return cache.get(r2Key) ?? null;
}

/** React binding: kicks the load and re-renders when it lands. */
export function useImageDims(r2Key: string | null): ImageDims | null {
  const subscribe = useCallback((cb: () => void) => {
    listeners.add(cb);
    return () => {
      listeners.delete(cb);
    };
  }, []);
  const getSnapshot = useCallback(
    () => (r2Key ? (cache.get(r2Key) ?? null) : null),
    [r2Key],
  );
  useEffect(() => {
    if (r2Key) void loadImageDims(r2Key);
  }, [r2Key]);
  return useSyncExternalStore(subscribe, getSnapshot);
}
